import { useMemo } from 'react';

import { expValEquals } from '@confluence/feature-experiments';
import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';
import { usePageSpaceKey, usePageContentId } from '@confluence/page-context';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import type { GlobalPageCard } from '@confluence/page-card/entry-points/cardTypes.ts';

import EndOfPageRecommendationQuery from '../__generated__/EndOfPageRecommendationQuery.graphql.ts';
import {
	type EndOfPageRecommendationQuery as EndOfPageRecommendationData,
	type EndOfPageRecommendationQueryVariables,
	type EndOfPageRecommendationQuery_getRecommendedPages_recommendedPages,
} from '../__types__/EndOfPageRecommendationQuery.ts';
import { transformAndOrderRecs } from '../transformers.ts';

export const usePageRecommendationData = (
	skip: boolean = false,
):
	| {
			loading: boolean;
			error: ApolloErrorInterop | undefined;
			data: undefined;
			hasRecs: boolean;
	  }
	| {
			loading: boolean;
			error: undefined;
			data: GlobalPageCard[] | undefined;
			hasRecs: boolean | undefined;
	  } => {
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();

	const baseOptions = {
		variables: {
			type: 'page',
			id: contentId ?? '',
			exp: 'end_of_page_v5',
			key: spaceKey ?? '',
			keyString: spaceKey ?? '',
		},
		context: { single: true },
		errorPolicy: 'all' as const,
		skip,
	};

	const { error, loading, data } = expValEquals(
		'confluence_frontend_stop_watching_queries',
		'isEnabled',
		true,
	)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useQueryNoFurtherUpdateInterop<
				EndOfPageRecommendationData,
				EndOfPageRecommendationQueryVariables
			>(EndOfPageRecommendationQuery, baseOptions)
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useQueryInterop<EndOfPageRecommendationData, EndOfPageRecommendationQueryVariables>(
				EndOfPageRecommendationQuery,
				baseOptions,
			);

	const pageCardData = useMemo(
		() =>
			transformAndOrderRecs(
				data?.getRecommendedPages
					?.recommendedPages as EndOfPageRecommendationQuery_getRecommendedPages_recommendedPages[],
			),
		[data],
	);

	if (error || skip) {
		return {
			loading: false,
			error,
			data: undefined,
			hasRecs: false,
		};
	}

	return {
		loading,
		error,
		data: pageCardData,
		hasRecs: pageCardData && pageCardData.length > 2,
	};
};
